// Only supported YouTube URLs become embeds. Never inject description HTML into the page.
export function parseYouTube(raw) {
  try {
    let value = String(raw || '').trim().replace(/&amp;/gi, '&');
    if (value.startsWith('//')) value = 'https:' + value;
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
    const host = url.hostname.toLowerCase();
    const parts = url.pathname.split('/').filter(Boolean);
    let id;
    if (['youtu.be', 'www.youtu.be'].includes(host)) id = parts[0];
    else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
      if (['embed', 'shorts', 'live'].includes(parts[0])) id = parts[1];
      else if (url.pathname === '/watch') id = url.searchParams.get('v');
    }
    if (!/^[a-zA-Z0-9_-]{11}$/.test(id || '')) return null;
    const offset = url.searchParams.get('start') || url.searchParams.get('t') || '';
    let start = 0;
    if (/^\d+$/.test(offset)) start = Number(offset);
    else if (/^(?:\d+h)?(?:\d+m)?(?:\d+s)?$/.test(offset)) {
      start = Number(offset.match(/(\d+)h/)?.[1] || 0) * 3600 + Number(offset.match(/(\d+)m/)?.[1] || 0) * 60 + Number(offset.match(/(\d+)s/)?.[1] || 0);
    }
    return {id, start: Number.isSafeInteger(start) && start < 86400 ? start : 0};
  } catch { return null; }
}

export function firstDescriptionVideo(html, doc = document) {
  const template = doc.createElement('template');
  template.innerHTML = String(html || ''); // Template content is inert; no frames or scripts are mounted.
  function visit(parent) {
    for (const node of parent.childNodes) {
      if (node.nodeType === 1) {
        if (['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(node.tagName)) continue;
        if (['IFRAME', 'A'].includes(node.tagName)) {
          const video = parseYouTube(node.getAttribute('src') || node.getAttribute('data-src') || node.getAttribute('href'));
          if (video) return video;
        }
        const nested = visit(node);
        if (nested) return nested;
      } else if (node.nodeType === 3) {
        for (const match of node.textContent.matchAll(/https?:\/\/(?:www\.|m\.)?(?:youtube(?:-nocookie)?\.com|youtu\.be)\/[^\s<>"']+/gi)) {
          const video = parseYouTube(match[0].replace(/[),.;]+$/, ''));
          if (video) return video;
        }
      }
    }
    return null;
  }
  return visit(template.content);
}

if (typeof customElements !== 'undefined' && !customElements.get('ns-description-video')) {
  customElements.define('ns-description-video', class extends HTMLElement {
    connectedCallback() {
      if (this.moving) return;
      if (!this.initialized) {
        try { this.video = firstDescriptionVideo(JSON.parse(this.querySelector('[data-video-description]').textContent)); }
        catch { return; }
        if (!this.video) return;
        this.initialized = true;
        this.desktopAnchor = document.createComment('description-video-desktop');
        this.before(this.desktopAnchor);
        this.scope = this.closest('.product');
        this.viewport = matchMedia('(max-width: 999px)');
        this.onViewport = () => { this.stop(false); this.place(); };
        this.render();
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-video-play]')) this.play();
          if (event.target.closest('[data-video-close]')) this.stop(true);
        });
        this.hidden = false;
      }
      this.viewport.addEventListener('change', this.onViewport);
      this.place();
    }
    disconnectedCallback() {
      if (this.moving) return;
      this.viewport?.removeEventListener('change', this.onViewport);
      this.stop(false);
    }
    place() {
      if (!this.scope?.isConnected || !this.desktopAnchor?.isConnected) return;
      const mobileTarget = this.scope.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
      const target = this.viewport.matches && mobileTarget ? mobileTarget : this.desktopAnchor;
      if (target.nextSibling === this) return;
      this.moving = true;
      target.after(this);
      this.moving = false;
    }
    render() {
      const title = this.dataset.productTitle || 'This saber';
      this.innerHTML = `<section class="ns-video-card" aria-label="Product video">
        <div class="ns-video-card__heading"><div><p class="ns-video-card__eyebrow">BEYOND THE PHOTOS</p><h3 class="ns-video-card__title"></h3></div><span class="ns-video-card__source"><svg aria-hidden="true" width="17" height="13" viewBox="0 0 24 18"><rect x="1" y="1" width="22" height="16" rx="5" fill="currentColor"/><path d="m10 5 6 4-6 4Z" fill="#090c0e"/></svg>YouTube</span></div>
        <div class="ns-video-card__screen">
          <button type="button" class="ns-video-card__poster" data-video-play>
            <img width="480" height="360" loading="lazy" decoding="async" alt="">
            <span class="ns-video-card__play" aria-hidden="true"><svg width="22" height="24" viewBox="0 0 22 24"><path d="M3 2L20 12 3 22Z" fill="currentColor"/></svg></span>
            <span class="ns-video-card__caption" aria-hidden="true">See this saber in action</span>
          </button>
        </div>
        <div class="ns-video-card__footer"><button type="button" data-video-close hidden>Close video</button><a target="_blank" rel="noopener noreferrer">Watch on YouTube</a></div>
      </section>`;
      this.querySelector('h3').textContent = title + ', up close.';
      this.querySelector('[data-video-play]').setAttribute('aria-label', 'Play video for ' + title);
      const image = this.querySelector('img');
      image.src = `https://i.ytimg.com/vi/${this.video.id}/hqdefault.jpg`;
      image.addEventListener('error', () => { image.hidden = true; }, {once:true});
      this.querySelector('a').href = `https://www.youtube.com/watch?v=${this.video.id}` + (this.video.start ? `&t=${this.video.start}s` : '');
    }
    play() {
      if (this.querySelector('iframe')) return;
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${this.video.id}?autoplay=1&playsinline=1&rel=0` + (this.video.start ? `&start=${this.video.start}` : '');
      frame.title = 'Video for ' + (this.dataset.productTitle || 'this product');
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      this.querySelector('[data-video-play]').hidden = true;
      this.querySelector('[data-video-close]').hidden = false;
      this.querySelector('.ns-video-card__screen').append(frame);
      frame.focus();
    }
    stop(restoreFocus) {
      if (!this.querySelector('iframe')) return;
      this.querySelector('iframe').remove();
      const play = this.querySelector('[data-video-play]');
      play.hidden = false;
      this.querySelector('[data-video-close]').hidden = true;
      if (restoreFocus) play.focus({preventScroll:true});
    }
  });
}
